import { CookieSetOptions } from "universal-cookie";
import { TK } from "../store/translations/translationKeys";
import * as Holidays from "date-holidays";
import { RecurringConfig, RecurringUnit } from "../models";
import { Moment } from "moment";
import moment from "moment";

export const ifEnter = (event: React.KeyboardEvent, action: () => void) => {
    if(event.keyCode === 13) action();
}
const charsMap: {[letter: string]: RegExp} = {
    a : /[\xE0-\xE6]/g,
    e : /[\xE8-\xEB]/g,
    i : /[\xEC-\xEF]/g,
    o : /[\xF2-\xF6]/g,
    u : /[\xF9-\xFC]/g,
    c : /\xE7/g,
    n : /\xF1/g,
};

const removeAccents = (string: string) => {
	for ( var letra in charsMap ) {
		string = string.replace( charsMap[letra], letra );
    }
    return string;
}

export const areIdentical = (source: string, compareTo: string) => {
    const compareSanitized = removeAccents(compareTo?.toLowerCase() ?? '');
    const sourceSanitized = removeAccents(source?.toLowerCase() ?? '');
    return !sourceSanitized.split(' ').some(valueWord => compareSanitized.indexOf(valueWord) < 0)
}

export const arrayWith = <T>(size: number, create: (i: number) => T): T[] => 
    Array.from(Array(size).keys()).map((_, i) => create(i));

export const isGuid = (v: string): boolean => /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/.test(v)

export const cookiesOptions: CookieSetOptions = {maxAge: 5*365*24*60*60}

// DATES

export const getWeekDayTranslationKey = (weekDay: number): TK => {
    switch(weekDay) {
      case 0: return TK.sunday;
      case 1: return TK.monday;
      case 2: return TK.tuesday;
      case 3: return TK.wendesday;
      case 4: return TK.thursday;
      case 5: return TK.friday;
      case 6: return TK.saturday;
      default: return TK.unknown;
    }
  }

export const parseTimeStringToMinutes = (datetime: string): number => (datetime?.length ?? 0) < 5 ? 0 :
    (parseInt(datetime.substr(0,2)) * 60 + parseInt(datetime.substr(3,2)));

export const minutesToTimeString = (minutes: number): string => 
  [
    minutes < 100*60 ? ("0" + Math.floor(minutes/60)).slice(-2) : Math.floor(minutes/60),
    ("0" + Math.ceil(minutes%60)).slice(-2),
  ].join(':')

export const addDayToDateString = (date: string, days: number): string => {
    var d = new Date(date);
    d.setDate(d.getDate() + days);
    return d.toISOString().substr(0,10);
}

export const addDays = (date: string, days: number): string => {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d.toISOString().substr(0, 10);
}

export const nextWorkingDay = (date: string): string => {
    const hd = new Holidays.default('PT');
    const d = new Date(date);
    do {
        d.setDate(d.getDate() + 1);
    }
    while(d.getDay() === 0 || (hd.isHoliday(d) as Holidays.Holiday).type === 'public');
    return d.toISOString().substr(0, 10);
}

export const minutesToTime = (minutes: number): string => 
    String(Math.floor(minutes/60)).padStart(2, '0') +
    ':' +
    String(minutes%60).padStart(2, '0')

export const minutesBetweenTimes = (start: string, end: string): number => {
  const startD = new Date(`1970-01-01 ${start}`)
  const endD = new Date(`1970-01-01 ${end}`)
  return Math.round((endD.getTime() - startD.getTime()) / 1000 / 60)
}

export const getAge = (dateOfBirth: Date|string) => {
  if(typeof dateOfBirth === 'string') dateOfBirth = new Date(dateOfBirth)
  var ageDifMs = Date.now() - dateOfBirth.getTime();
  var ageDate = new Date(ageDifMs); // miliseconds from epoch
  return Math.abs(ageDate.getUTCFullYear() - 1970);
}

export const daysBetween = (d1: Date, d2: Date) => {
  const diff = d1.getTime() - d2.getTime()
  return Math.ceil(diff/(1000 * 3600 * 24))
}

export const parseDateString = (date: string) => {
  const utcSufix = '1970-01-01T00:00:00.000Z'
  const dateUtc = date + utcSufix.substring(date.length)
  return new Date(dateUtc)
}

export const getDateString = (date: Date): string => `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
export const tryDateString = (date?: Date): string|undefined => date ? getDateString(date) : undefined
export const getNowDateString = (): string => getDateString(new Date)

const isValidDate = (date: Date): boolean => !Number.isNaN(date.getTime());

export const toDateOnlyString = (value: unknown): string | undefined => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed.length) return undefined;

    const isoDatePrefix = trimmed.match(/^(\d{4}-\d{2}-\d{2})/)?.[1];
    if (isoDatePrefix) return isoDatePrefix;

    const parsed = new Date(trimmed);
    return isValidDate(parsed) ? getDateString(parsed) : undefined;
  }

  if (value instanceof Date) {
    return isValidDate(value) ? getDateString(value) : undefined;
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    const parsed = new Date(value);
    return isValidDate(parsed) ? getDateString(parsed) : undefined;
  }

  if (typeof value === 'object' && value !== null) {
    const dateLike = value as { toDate?: () => Date; toISOString?: () => string };
    if (typeof dateLike.toDate === 'function') {
      return toDateOnlyString(dateLike.toDate());
    }
    if (typeof dateLike.toISOString === 'function') {
      return toDateOnlyString(dateLike.toISOString());
    }
  }

  return undefined;
}

export const parseDateOnlyToLocalDate = (value: unknown): Date | undefined => {
  const dateOnly = toDateOnlyString(value);
  if (!dateOnly) return undefined;

  const [year, month, day] = dateOnly.split('-').map(v => parseInt(v, 10));
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) return undefined;

  const parsed = new Date(year, month - 1, day);
  if (!isValidDate(parsed)) return undefined;

  if (parsed.getFullYear() !== year || parsed.getMonth() !== month - 1 || parsed.getDate() !== day) return undefined;

  return parsed;
}



export const getRecurringDates = (
  recurring: RecurringConfig, // Recuring config
  date: Moment | string, // Original date
  from: Moment | string, // Get recuring dates interval
  to: Moment | string, // Get recuring dates interval
): Moment[] => {
    if (!recurring) return [];

    const dateMoment = typeof date === 'string' ? moment.utc(date) : date;
    const fromMoment = typeof from === 'string' ? moment.utc(from) : from;
    const toMoment = (typeof to === 'string' ? moment.utc(to) : to).clone().endOf('day');

    const dates: Moment[] = [];
    const endDate = recurring.endDate ? moment.min(toMoment, moment.utc(recurring.endDate)) : toMoment.endOf('day');
    
    const currentDate = dateMoment;
    
    while (currentDate.isSameOrBefore(endDate)) {
        if (currentDate.isSameOrAfter(fromMoment)) {
            dates.push(currentDate.clone())
        }

        switch(recurring.unit) {
            case RecurringUnit.Days:
                currentDate.add(recurring.value, 'days');
                break;
            case RecurringUnit.Weeks:
                currentDate.add(recurring.value, 'weeks');
                break;
            case RecurringUnit.Months:
                currentDate.add(recurring.value, 'months');
                break;
        }
    }
    
    return dates;
}

// GUID

export class Guid {
    static newGuid() {
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        var r = Math.random() * 16 | 0,
          v = c === 'x' ? r : ((r & 0x3) | 0x8);
        return v.toString(16);
      });
    }
  }

// ARRAYS

export const moveItem = <T>(data: Array<T>, from: number, to: number) => {
  const f = data.splice(from, 1)[0];
  data.splice(to, 0, f);
  return data;
}

// FILE

export const fileToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, _) => {
    const reader = new FileReader();
    reader.onloadend = () => typeof reader.result === 'string' && resolve(reader.result);
    reader.readAsDataURL(blob);
  });
}

// COLORS

const hexLuminance = (hex: string): number => {
  try {
    const c = hex.startsWith('#') ? hex.slice(1) : hex
    const num = parseInt(c, 16)
    const r = (num >> 16) & 0xff
    const g = (num >> 8) & 0xff
    const b = num & 0xff
    return 0.2126 * r + 0.7152 * g + 0.0722 * b
  } catch {
    return 0
  }
}

// Returns '#FFFFFF' or '#000000' based on perceived luminance of the given hex color
export const getContrastColor = (hex: string): '#FFFFFF' | '#000000' =>
  hexLuminance(hex) > 140 ? '#000000' : '#FFFFFF'

export const isLightColor = (hex: string): boolean => hexLuminance(hex) > 140

export const lightenDarkenColor = (color: string, amount: number) => {
  try {
    let usePound = false;

    if (color[0] == "#") {
        color = color.slice(1);
        usePound = true;
    }

    var num = parseInt(color,16);

    var r = (num >> 16) + amount;

    if (r > 255) r = 255;
    else if  (r < 0) r = 0;

    var b = ((num >> 8) & 0x00FF) + amount;

    if (b > 255) b = 255;
    else if  (b < 0) b = 0;

    var g = (num & 0x0000FF) + amount;

    if (g > 255) g = 255;
    else if (g < 0) g = 0;

    return (usePound ? "#" : "") + (g | (b << 8) | (r << 16)).toString(16).padStart(6, '0')
  } catch {
    console.error('Invalid color provided to lightenDarkenColor:', color);
    return color;
  }
}
